import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useCallSelector } from './selectors'
import { CALL_HISTORY_QUERY_KEY } from './useCallHistorySearch'

/**
 * L'ÉVÉNEMENT « FIN D'APPEL » — le SEUL déclencheur de rafraîchissement des
 * listes de la téléphonie v2.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE HOOK EST MONTÉ DANS LE PROVIDER, ET NULLE PART AILLEURS
 * ══════════════════════════════════════════════════════════════════════════
 *
 * La règle du cahier — « rafraîchi par événement, jamais par scrutation » —
 * était appliquée par chaque liste, chacune surveillant la retombée du poste
 * au repos de son côté. Ça ne pouvait pas marcher pour l'historique de l'agent :
 * son panneau n'est rendu que sur la porte manuelle AU REPOS, donc il est
 * DÉMONTÉ pendant tout l'appel et ne voit jamais passer la transition qu'il
 * était censé guetter. Il rechargeait alors à chaque montage — c'est-à-dire à
 * chaque bascule de porte, appel ou pas.
 *
 * L'observateur est donc remonté à l'endroit qui, lui, ne se démonte jamais :
 * le provider. Il n'affiche rien et ne charge rien — il INVALIDE, et React
 * Query décide : une liste à l'écran se recharge tout de suite, une liste
 * absente attend d'être regardée.
 *
 * ⚠️ Une liste « invalidée » n'est PAS une liste rechargée. C'est ce qui rend
 * la règle tenable : pendant une session de campagne, chaque fin d'appel
 * invalide l'historique de l'agent sans qu'aucune requête ne parte, puisque
 * personne ne le regarde. Il sera frais au moment exact où l'agent y revient.
 */

/** Clés de listes que la fin d'un appel rend potentiellement périmées. */
const REFRESHED_ON_CALL_END: readonly (readonly unknown[])[] = [
  // Historique de l'agent (porte manuelle) — l'appel qui vient de finir y entre.
  CALL_HISTORY_QUERY_KEY,
  // Bandeau de session : compteurs du jour et rappels dus.
  ['telephony', 'today-stats'],
  ['telephony', 'upcoming-callbacks'],
  // Onglet « Historique » de la fiche : le contact a une tentative de plus.
  ['telephony', 'contact-call-history'],
]

export function useCallEndRefresh(enabled: boolean): void {
  const phase = useCallSelector((state) => state.phase)
  const queryClient = useQueryClient()

  /**
   * `useSyncExternalStore` produit plusieurs rendus par transition : sans la
   * phase précédente, l'invalidation se rejouerait à chaque rendu passé en
   * `idle`.
   */
  const previousPhase = useRef(phase)

  useEffect(() => {
    const wasBusy = previousPhase.current !== 'idle'
    previousPhase.current = phase
    if (!enabled) return
    if (phase !== 'idle' || !wasBusy) return
    REFRESHED_ON_CALL_END.forEach((queryKey) => {
      void queryClient.invalidateQueries({ queryKey })
    })
  }, [phase, enabled, queryClient])
}
